import { Controller } from "@hotwired/stimulus"

/*
 * Gates the Amazon Key access point list.
 *
 * Amazon needs a named access point and driver notes for every entry before the
 * setup is worth submitting, so the submit button stays disabled until every
 * row is complete. Add and Remove follow the shape of the list rather than
 * being always-on: you cannot remove the last row, only the last row offers
 * Add, and a property with a single access point to choose from never offers
 * Add.
 *
 * On the property settings page the button saves every setting, and the server
 * only holds a save to these rules when the section was edited or the
 * integration is being switched on (`Buildings::AmazonKeySetupForm#changed?`).
 * The gate here follows the same test, so a door that went offline or a legacy
 * row without notes does not lock the rest of the page (MOM-931).
 *
 * A photo is what actually makes an access point usable by drivers, but it is
 * never demanded up front: submitting without one is allowed, after confirming
 * that the photo is still outstanding for those access points.
 *
 * An offline access point is the one thing no amount of filling in fixes, so it
 * is called out on the row and blocks the activation modal outright. So is one
 * Amazon could only unlock through a legacy keypad: those are kept out of the
 * dropdown, but a row saved before that rule still has to say why it has to go.
 * On the settings page both are left to the server, which refuses them by name.
 */
export default class extends Controller {
  static targets = [
    "row",
    "accessPoint",
    "notes",
    "addButton",
    "removeButton",
    "offlineError",
    "legacyKeypadError"
  ]
  static values = {
    singleAccessPoint: Boolean,
    submitSelector: String,
    // Selector for the checkbox that turns the integration on. On the property
    // settings page the submit button saves every setting, not just this
    // section, so an incomplete Amazon Key list must only block it while the
    // integration is actually being switched on.
    enabledSelector: String,
    // Terms checkbox, where the surface has one. It lives in the modal footer,
    // outside this controller's element.
    termsSelector: String,
    // Access points the server would refuse, so the button can go dead before a
    // round trip rather than after one.
    offlineAccessPointIds: Array,
    legacyKeypadAccessPointIds: Array,
    // Access point id -> the photo it already has, so a row follows its
    // selection instead of showing an empty box for a configured access point.
    accessPointPhotoUrls: Object,
    // Whether the integration is already on for the property. Switching it on
    // is itself a change the server validates, even with no rows submitted.
    integrationEnabled: Boolean,
    // Access point id -> the notes on file for every access point Amazon
    // currently unlocks: the list as the server knows it, to tell an edited
    // section from an untouched one.
    currentNotes: Object,
    missingPhotoTitle: String,
    missingPhotoBody: String,
    missingPhotoAccept: String,
    missingPhotoCancel: String
  }

  connect() {
    this.refresh()
  }

  disconnect() {
    this.clearMissingPhotoConfirmation()
  }

  refresh() {
    // The template row is inert markup until nested-form clones it, but its
    // targets are still registered, so filter to rows actually in the list.
    const rows = this.rowTargets.filter((row) => !this.inTemplate(row))

    // Add belongs to the list and lives below it; Remove belongs to each row.
    if (this.hasAddButtonTarget) {
      this.toggle(this.addButtonTarget, !this.singleAccessPointValue)
    }

    rows.forEach((row) => {
      this.toggle(this.targetIn(row, "removeButton"), rows.length > 1)
      this.toggle(this.targetIn(row, "offlineError"), this.rowIsOffline(row))
      this.toggle(this.targetIn(row, "legacyKeypadError"), this.rowIsLegacyKeypad(row))
      this.showStoredPhoto(row)
    })

    this.updateSubmitState(rows)
    this.updateMissingPhotoConfirmation(rows)
  }

  updateSubmitState(rows) {
    const submit = this.submitButton
    if (!submit) return

    if (!this.sectionEdited(rows)) {
      submit.disabled = false
      return
    }

    submit.disabled =
      rows.length === 0 ||
      !this.termsAccepted ||
      !rows.every((row) => this.rowIsComplete(row))
  }

  // Turbo reads these off the submitter when the form is submitted.
  updateMissingPhotoConfirmation(rows) {
    const submit = this.submitButton
    if (!submit) return

    // On the property settings page this button saves every setting, so a
    // section nobody touched has nothing to warn about.
    const anyMissing = this.sectionEdited(rows) && rows.some((row) => !this.hasPhoto(row))

    if (!anyMissing) {
      this.clearMissingPhotoConfirmation()
      return
    }

    submit.dataset.turboConfirm = this.missingPhotoBodyValue
    submit.dataset.confirmTitle = this.missingPhotoTitleValue
    submit.dataset.confirmAccept = this.missingPhotoAcceptValue
    submit.dataset.confirmCancel = this.missingPhotoCancelValue
  }

  clearMissingPhotoConfirmation() {
    const submit = this.submitButton
    if (!submit) return

    delete submit.dataset.turboConfirm
    delete submit.dataset.confirmTitle
    delete submit.dataset.confirmAccept
    delete submit.dataset.confirmCancel
  }

  // Whether this save submits the section at all, mirroring what the server
  // validates: the section is switched off, so nothing is submitted; the
  // integration is being switched on, so everything is; or the list differs
  // from what is on file. Without an enabling checkbox (the activation modal)
  // the section is the whole form.
  sectionEdited(rows) {
    const enabled = this.enabledCheckbox
    if (!enabled) return true
    if (!enabled.checked) return false
    if (!this.integrationEnabledValue) return true

    const listed = rows.filter((row) => this.targetIn(row, "accessPoint")?.value)
    const listedIds = listed.map((row) => this.targetIn(row, "accessPoint").value).sort()
    const currentIds = Object.keys(this.currentNotesValue).sort()

    if (listedIds.length !== currentIds.length) return true
    if (listedIds.some((id, index) => id !== currentIds[index])) return true

    return listed.some((row) => this.rowEdited(row))
  }

  // The server compares a row against the access point it names, so a row that
  // was only cleared and filled back in with the same notes is not a change.
  rowEdited(row) {
    const fileInput = row.querySelector("[data-image-upload-target='fileInput']")
    const removeInput = row.querySelector("[data-image-upload-target='removeImageInput']")
    if (fileInput?.files?.length > 0 || removeInput?.checked) return true

    const id = this.targetIn(row, "accessPoint").value
    const notes = this.targetIn(row, "notes")?.value ?? ""

    return this.normalizeNotes(notes) !== this.normalizeNotes(this.currentNotesValue[id])
  }

  // What `AccessPoint` does to notes before storing them.
  normalizeNotes(value) {
    return (value ?? "").replace(/\r\n/g, "\n")
  }

  // A photo is not part of completeness: a row without one still saves, the
  // access point just stays inactive to drivers until a photo arrives.
  rowIsComplete(row) {
    const accessPoint = this.targetIn(row, "accessPoint")
    const notes = this.targetIn(row, "notes")

    if (!accessPoint?.value || !notes?.value.trim()) return false

    // The activation modal has nothing else to save, so an offline or
    // legacy-keypad access point stops it before a round trip. The settings
    // page leaves that refusal to the server: its Save covers every setting,
    // the row already says why the door cannot be used, and the error comes
    // back naming the access point rather than as a dead button with no
    // explanation next to it.
    return !(this.blocksUnusableAccessPoints && (this.rowIsOffline(row) || this.rowIsLegacyKeypad(row)))
  }

  get blocksUnusableAccessPoints() {
    return this.enabledCheckbox === null
  }

  // Picking a different access point makes a pending removal meaningless: it
  // was aimed at the photo of the one being replaced, and leaving it armed
  // would wipe the photo of the one just chosen.
  selectAccessPoint(event) {
    const removeInput = event.target
      .closest(".amazon-key-row")
      ?.querySelector("[data-image-upload-target='removeImageInput']")

    if (removeInput?.checked) removeInput.checked = false

    this.refresh()
  }

  // Only an untouched photo field follows the selection — a file the manager
  // just picked, or a removal they just made, is theirs to display.
  showStoredPhoto(row) {
    const preview = row.querySelector("[data-image-upload-target='imagePreview']")
    if (!preview) return

    const fileInput = row.querySelector("[data-image-upload-target='fileInput']")
    const removeInput = row.querySelector("[data-image-upload-target='removeImageInput']")
    if (fileInput?.files?.length > 0 || removeInput?.checked) return

    const url = this.accessPointPhotoUrlsValue[this.targetIn(row, "accessPoint")?.value]

    // The empty box and the preview are both driven off the `src` attribute
    // being present, so it has to be removed rather than blanked.
    if (url) {
      preview.setAttribute("src", url)
    } else {
      preview.removeAttribute("src")
    }
  }

  rowIsOffline(row) {
    return this.rowSelects(row, this.offlineAccessPointIdsValue)
  }

  rowIsLegacyKeypad(row) {
    return this.rowSelects(row, this.legacyKeypadAccessPointIdsValue)
  }

  rowSelects(row, accessPointIds) {
    const accessPoint = this.targetIn(row, "accessPoint")
    if (!accessPoint?.value) return false

    return accessPointIds.includes(Number(accessPoint.value))
  }

  hasPhoto(row) {
    const preview = row.querySelector("[data-image-upload-target='imagePreview']")
    const fileInput = row.querySelector("[data-image-upload-target='fileInput']")
    const removeInput = row.querySelector("[data-image-upload-target='removeImageInput']")

    if (removeInput?.checked) return false
    if (fileInput?.files?.length > 0) return true

    return Boolean(preview?.getAttribute("src"))
  }

  // The checkbox that turns the integration on, where the surface has one. The
  // activation modal has none: there the section is always the thing submitted.
  get enabledCheckbox() {
    return this.elementMatching(this.enabledSelectorValue, this.hasEnabledSelectorValue)
  }

  get termsAccepted() {
    return Boolean(this.elementMatching(this.termsSelectorValue, this.hasTermsSelectorValue)?.checked ?? true)
  }

  get submitButton() {
    return this.elementMatching(this.submitSelectorValue, this.hasSubmitSelectorValue)
  }

  elementMatching(selector, present) {
    if (!present || selector === "") return null

    return document.querySelector(selector)
  }

  // Targets repeat per row, so `this.<name>Target` would always answer for the
  // first one — they have to be looked up within the row being examined.
  targetIn(row, name) {
    return row.querySelector(`[data-amazon-key-setup-target='${name}']`)
  }

  inTemplate(element) {
    return element.closest("template") !== null
  }

  toggle(element, visible) {
    if (element) element.hidden = !visible
  }
}
